import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { addUser, updateUser } from "../../../services/Admin";
import { errorToast, successToast } from "../../Toasts/Toasts";
import { userRoleConstants } from "../../../utils/constants";
import { getAdminUsersApi } from "../../../redux/features/adminUserSlice";
import "react-phone-number-input/style.css";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";

const UserFormModal = ({
  show,
  setShow,
  userData = {},
  isEdit = false,
  masterData,
  setId,
  currentSize
}) => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneCodeError, setPhoneCodeError] = useState(false);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    if (show) {
      if (isEdit && userData && userData.id) {
        let pCode = userData.phone_code || "";
        if (pCode && !pCode.startsWith("+")) pCode = "+" + pCode;

        const pNumber = userData.phone_number || userData.phone || "";

        setPhoneCode(pCode);
        setPhoneNumber(pNumber);

        const fullPhone = pCode + pNumber;

        const editData = {
          ...userData,
          phone_number: fullPhone,
          address: userData.address,
          role: {
            label: userData.role || userData["Role.name"],
            value: userData.role_id
          },
          country: {
            label: userData.country_name || userData["Country.name"],
            value: userData.country_id
          },
          state: {
            label: userData.state_name || userData["State.state_name"],
            value: userData.state_id
          },
        };

        Object.keys(editData).forEach((key) => {
          setValue(key, editData[key]);
        });

        if (userData.country_id) {
          const country = masterData?.countries?.find(c => c.id === userData.country_id);
          if (country) setId(country.countryCode);
        }
      } else {
        setPhoneCode("");
        setPhoneNumber("");
        setPhoneCodeError(false);
        reset({
          name: "",
          email: "",
          phone_number: "",
          role: null,
          address: "",
          country: null,
          state: null,
          city: "",
          zip: ""
        });
      }
    }
  }, [show, isEdit, userData?.id, setValue, reset]);

  const onFormSubmit = async (data) => {
    try {
      if (!isEdit && !phoneNumber) {
        setPhoneCodeError(true);
        return;
      }

      const payload = {
        name: data.name,
        email: data.email,
        phone_number: phoneNumber,
        phone_code: phoneCode,
        address: data.address,
        city: data.city,
        zip: data.zip,
        role_id: data.role?.value,
        country_id: data.country?.value,
        state_id: data.state?.value,
      };

      let response;
      const button = document.getElementById("submit-user-btn");
      if (button) button.disabled = true;

      if (isEdit) {
        response = await updateUser(userData.id, payload);
      } else {
        response = await addUser(payload);
      }

      if (button) button.disabled = false;

      if (response?.success) {
        successToast(response?.message || `User ${isEdit ? "updated" : "added"} successfully`);
        setShow(false);
        dispatch(getAdminUsersApi({ filteredData: {}, page: 1, size: currentSize || 10 }));
      } else {
        errorToast(response?.message || "Something went wrong");
      }
    } catch (error) {
      errorToast(error?.response?.data?.message || "An error occurred");
      const button = document.getElementById("submit-user-btn");
      if (button) button.disabled = false;
    }
  };

  const handlePhoneChange = (value) => {
    if (!value) {
      setPhoneCode("");
      setPhoneNumber("");
      setValue("phone_number", "");
      return;
    }

    const stringValue = String(value);
    const parsedPhoneNumber = parsePhoneNumber(stringValue);

    if (parsedPhoneNumber) {
      setPhoneCode(`+${parsedPhoneNumber?.countryCallingCode}`);
      setPhoneNumber(parsedPhoneNumber?.nationalNumber);
    } else if (stringValue.startsWith("+")) {
      setPhoneCode(stringValue);
      setPhoneNumber("");
    }

    setValue("phone_number", value, { shouldValidate: true });
    setPhoneCodeError(false);
  };

  const handleClose = () => {
    setShow(false);
  };

  const roleOptions = [
    { label: "Manager", value: userRoleConstants.MANAGER_ID },
    { label: "Supervisor", value: userRoleConstants.SUPERVISOR_ID },
    { label: "Accountant", value: userRoleConstants.ACCOUNTANT_ID },
    { label: "Procurement", value: userRoleConstants.PROCUREMENT_ID },
  ];

  const countryOptions = masterData?.countries?.map((c) => ({
    label: c.name,
    value: c.id,
    code: c.countryCode,
  }));

  const stateOptions = masterData?.states?.map((s) => ({
    label: s.name,
    value: s.id,
  }));

  return (
    <Modal show={show} size="md" centered onHide={handleClose}>
      <div className="modal-simple modal-edit-user">
        <div className="modal-content p-3 p-md-3">
          <Modal.Body className="p-2">
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            />
            <div className="text-left mb-4">
              <h3 className="mb-2">{isEdit ? "Edit User" : "Add User"}</h3>
            </div>

            <form onSubmit={handleSubmit(onFormSubmit)}>
              <div className="row">
                <div className="col-lg-12 mb-3">
                  <label className="form-label">
                    User Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("name", { required: "User name is required" })}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    disabled={isEdit}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Invalid email",
                      },
                    })}
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email.message}</small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{ required: "Phone number is required" }}
                    render={({ field }) => (
                      <>
                        <PhoneInput
                          maxLength={20}
                          className="form-control"
                          international
                          withCountryCallingCode
                          countryCallingCodeEditable={false}
                          defaultCountry="AF"
                          value={field.value}
                          onChange={(value) => {
                            handlePhoneChange(value);
                          }}
                        />
                        {phoneCodeError && <small className="text-danger">Required</small>}
                        {errors?.phone_number && (
                          <small className="text-danger">{errors.phone_number.message}</small>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">
                    Role <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: "Role is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={roleOptions}
                        isSearchable
                        isDisabled={isEdit}
                        classNamePrefix="react-select"
                      />
                    )}
                  />
                  {errors.role && (
                    <small className="text-danger">{errors.role.message}</small>
                  )}
                </div>

                <div className="col-lg-12 mb-3">
                  <label className="form-label">Address <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("address", { required: "Address is required" })}
                  />
                  {errors.address && (
                    <small className="text-danger">
                      {errors.address.message}
                    </small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">Country <span className="text-danger">*</span></label>
                  <Controller
                    name="country"
                    rules={{ required: "Country is required" }}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti={false}
                        options={countryOptions}
                        isSearchable
                        onChange={(val) => {
                          field.onChange(val);
                          setId(val?.code);
                          setValue("state", null);
                        }}
                      />
                    )}
                  />
                  {errors.country && (
                    <small className="text-danger">{errors.country.message}</small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">State <span className="text-danger">*</span></label>
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: "State is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={stateOptions}
                        isSearchable
                        classNamePrefix="react-select"
                      />
                    )}
                  />
                  {errors.state && (
                    <small className="text-danger">{errors.state.message}</small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">City <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("city", { required: "City is required" })}
                  />
                  {errors.city && (
                    <small className="text-danger">{errors.city.message}</small>
                  )}
                </div>

                <div className="col-lg-6 mb-3">
                  <label className="form-label">Zip <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("zip", { required: "Zip is required" })}
                  />
                  {errors.zip && (
                    <small className="text-danger">{errors.zip.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-12 text-center mt-4">
                  <button
                    type="submit"
                    id="submit-user-btn"
                    className="btn btn-primary rounded-pill me-sm-3 me-1 waves-effect waves-light"
                  >
                    {isEdit ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-label-secondary rounded-pill btn-reset waves-effect"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

export default UserFormModal;
